import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CustomHtmlComingSoon from "./CustomHtmlComingSoon";
import { resolveLogoUrl } from "@/lib/branding";
import { CheckCircle, Clock, MapPin, Phone, Recycle, ShieldCheck, Sparkle, Star, Truck } from "@phosphor-icons/react";

export default function ComingSoonView({ tenant: tenantProp }) {
  const isPreviewMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("_preview") === "1";
  const [previewOverrides, setPreviewOverrides] = useState({});
  const tenant = isPreviewMode ? { ...tenantProp, ...previewOverrides } : tenantProp;
  const b = tenant.branding || {};
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    street: "",
    zip: "",
    city: "",
    privacy_ack: false,
    newsletter_optin: true,
  });
  const [cityOptions, setCityOptions] = useState([]);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [promos, setPromos] = useState([]);

  // Draft-Modus: höre auf postMessage vom Editor
  useEffect(() => {
    if (!isPreviewMode) return;
    try { window.parent?.postMessage({ type: "tonneva-preview-ready" }, window.location.origin); } catch (err) { console.warn("[preview] postMessage failed", err); }
    const onMessage = (e) => {
      if (e.data?.type === "tonneva-preview-update" && e.data.data) {
        setPreviewOverrides(e.data.data);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isPreviewMode]);

  useEffect(() => {
    api.get(`/promos/public/${tenantProp.slug}`).then(r => setPromos(r.data || [])).catch(() => {});
  }, [tenantProp.slug]);

  // PLZ → Ort-Vorschläge aus konfigurierten Servicegebieten
  useEffect(() => {
    const z = form.zip.trim();
    if (z.length < 2) { setCityOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/portal/${tenantProp.slug}/cities-for-zip?zip=${encodeURIComponent(z)}`);
        const cities = r.data?.cities || [];
        setCityOptions(cities);
        // Auto-fill wenn genau 1 Match und Feld leer oder mismatched
        if (cities.length === 1 && !form.city) {
          setForm((f) => ({ ...f, city: cities[0].city, zip: cities[0].zip }));
        }
      } catch { setCityOptions([]); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.zip, tenantProp.slug]);

  const featuredPromo = promos[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) {
      setStatus({ ok: false, msg: "Bitte gültige E-Mail-Adresse angeben" });
      return;
    }
    if (!form.first_name || !form.last_name) {
      setStatus({ ok: false, msg: "Bitte Vor- und Nachnamen angeben" });
      return;
    }
    if (!form.zip || !form.city) {
      setStatus({ ok: false, msg: "Bitte PLZ und Ort angeben" });
      return;
    }
    if (!form.privacy_ack) {
      setStatus({ ok: false, msg: "Bitte Datenschutzerklärung bestätigen" });
      return;
    }
    setSending(true);
    try {
      await api.post(`/portal/${tenantProp.slug}/leads`, {
        ...form,
        source: "coming_soon",
        reason: "coming_soon",
      });
      setStatus({ ok: true, msg: "Danke! Sobald wir starten, informieren wir Sie zuerst." });
      setForm({
        first_name: "", last_name: "", email: "",
        street: "", zip: "", city: "",
        privacy_ack: false, newsletter_optin: true,
      });
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || "Fehler bei der Anmeldung" });
    } finally {
      setSending(false);
    }
  };

  const primary = b.primary_color || "#2A86A8";
  const secondary = b.secondary_color || "#3FA64E";
  const dateStr = tenant.service_available_from ? new Date(tenant.service_available_from).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" }) : null;
  const availabilityText = tenant.coming_soon_availability_label || (dateStr ? `ab ${dateStr}` : null);
  const heroImage = tenant.coming_soon_hero_image_url;
  const headline = tenant.coming_soon_headline || `Wir starten bald in ${tenant.city || "Ihrer Region"}.`;
  const subheadline = tenant.coming_soon_subheadline;
  const ctaText = tenant.coming_soon_cta_text || "Jetzt vormerken lassen";
  const showNewsletter = tenant.coming_soon_show_newsletter !== false;
  const showContact = tenant.coming_soon_show_contact !== false;
  const features = tenant.coming_soon_features || [];

  // Custom-HTML-Variante — rendert das vom Tenant hinterlegte HTML 1:1 inkl. Lead-Formular-Hook
  if (tenant.coming_soon_custom_html) {
    return <CustomHtmlComingSoon tenant={tenant} />;
  }

  return (
    <div className="min-h-screen font-tenant text-tenant-text relative overflow-hidden" style={{ fontFamily: `var(--tenant-font)` }}>
      {/* Background gradient */}
      <div className="absolute inset-0 -z-10" style={{
        background: `linear-gradient(135deg, ${primary}15 0%, ${secondary}15 100%)`,
      }} />
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-20 -z-10" style={{ background: primary }} />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl opacity-20 -z-10" style={{ background: secondary }} />

      <div className="max-w-5xl mx-auto px-6 py-16 md:py-24" data-testid="coming-soon-view">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-10" />
            ) : (
              <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: primary }}>
                <Recycle size={22} weight="bold" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight text-lg">{tenant.name}</div>
              <div className="text-xs text-zinc-500">{tenant.city}</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1">
            <Link to={`/portal/${tenant.slug}/karte`} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md hover:bg-white/60 text-zinc-600" data-testid="header-map-link">
              Karte
            </Link>
            <Link to={`/portal/${tenant.slug}/referenzen`} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md hover:bg-white/60 text-zinc-600" data-testid="header-references-link">
              Referenzen
            </Link>
          </div>
        </div>

        <div className={`grid ${heroImage ? "md:grid-cols-2 gap-12 items-center" : ""}`}>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-6" style={{ background: `${primary}20`, color: primary }}>
              <Clock size={12} weight="bold" /> Bald verfügbar
              {availabilityText && <span className="text-zinc-600 normal-case font-normal">· {availabilityText}</span>}
            </div>

            <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4" data-testid="coming-soon-headline">
              {headline}
            </h1>
            {subheadline && (
              <p className="text-xl md:text-2xl text-zinc-600 mb-6 font-medium" data-testid="coming-soon-subheadline">
                {subheadline}
              </p>
            )}

            {featuredPromo && (
              <div className="mb-8 p-5 rounded-2xl border-2 border-dashed inline-flex items-start gap-4" style={{ borderColor: secondary, background: `${secondary}10` }} data-testid="coming-soon-promo">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: secondary }}>
                  <span className="text-white font-bold text-lg">
                    {featuredPromo.discount_percent ? `-${featuredPromo.discount_percent}%` : `-€${Math.round(featuredPromo.discount_cents / 100)}`}
                  </span>
                </div>
                <div>
                  <div className="font-heading text-lg font-bold" style={{ color: primary }}>Early-Bird-Aktion: {featuredPromo.code}</div>
                  <div className="text-sm text-zinc-700 mt-0.5">{featuredPromo.description}</div>
                  {featuredPromo.max_uses && (
                    <div className="text-xs text-zinc-500 mt-1">
                      Noch <strong>{featuredPromo.max_uses - featuredPromo.used_count}</strong> von {featuredPromo.max_uses} Codes verfügbar
                    </div>
                  )}
                </div>
              </div>
            )}

            {tenant.coming_soon_message && (
              <div className="text-lg text-zinc-600 mb-8 leading-relaxed whitespace-pre-line" data-testid="coming-soon-message">
                {tenant.coming_soon_message}
              </div>
            )}

            {features.length > 0 && (
              <ul className="space-y-2 mb-8" data-testid="coming-soon-features">
                {features.map((feat, i) => (
                  <li key={`${i}-${feat}`} className="flex items-start gap-2 text-sm text-zinc-700">
                    <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold" style={{ background: secondary }}>✓</span>
                    {feat}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {heroImage && (
            <div className="rounded-2xl overflow-hidden shadow-2xl" data-testid="coming-soon-hero-image">
              <img src={heroImage} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>

        {/* Feature-Grid (Warum GTR? / Warum wir?) */}
        {(tenant.coming_soon_feature_grid || []).length > 0 && (
          <section className="mt-16 md:mt-20" data-testid="coming-soon-feature-grid">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-8 text-center">
              {tenant.coming_soon_feature_grid_title || "Warum wir?"}
            </h2>
            <div className={`grid gap-6 ${(tenant.coming_soon_feature_grid || []).length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
              {tenant.coming_soon_feature_grid.map((item, i) => {
                const iconMap = { sparkle: Sparkle, "map-pin": MapPin, recycle: Recycle, shield: ShieldCheck, clock: Clock, leaf: Recycle, heart: Star, star: Star, truck: Truck, smiley: CheckCircle };
                const IconCmp = iconMap[item.icon] || Sparkle;
                return (
                  <div key={`${i}-${item.title || ""}`} className="bg-white/70 backdrop-blur-sm border border-black/10 rounded-2xl p-5 md:p-6" data-testid={`feature-grid-${i}`}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${secondary}20`, color: secondary }}>
                      <IconCmp size={22} weight="bold" />
                    </div>
                    <div className="font-heading text-base md:text-lg font-bold mb-1.5">{item.title}</div>
                    {item.description && <div className="text-sm text-zinc-600 leading-relaxed">{item.description}</div>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Prozess-Schritte (Drei Schritte. Kein Aufwand.) */}
        {(tenant.coming_soon_process_steps || []).length > 0 && (
          <section className="mt-16 md:mt-20" data-testid="coming-soon-process">
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-8 text-center">
              {tenant.coming_soon_process_title || "So einfach geht's."}
            </h2>
            <div className={`grid gap-6 ${tenant.coming_soon_process_steps.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              {tenant.coming_soon_process_steps.map((step, i) => (
                <div key={`${i}-${step.title || ""}`} className="relative bg-white/80 backdrop-blur-sm border border-black/10 rounded-2xl p-6 md:p-8" data-testid={`process-step-${i}`}>
                  <div className="absolute -top-4 -left-2 w-12 h-12 rounded-full flex items-center justify-center text-white font-heading font-bold text-lg shadow-lg" style={{ background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)` }}>
                    {i + 1}
                  </div>
                  <div className="font-heading text-lg md:text-xl font-bold mb-2 mt-2">{step.title}</div>
                  {step.description && <div className="text-sm text-zinc-600 leading-relaxed">{step.description}</div>}
                </div>
              ))}
            </div>
          </section>
        )}

        {showNewsletter && (
          <form onSubmit={submit} className="mt-10 bg-white/80 backdrop-blur-md border border-black/10 rounded-2xl p-6 md:p-8 space-y-4 shadow-lg" data-testid="coming-soon-lead-form">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle size={18} weight="bold" style={{ color: secondary }} />
              <h3 className="font-heading text-xl font-bold">Als Erste/r informiert werden</h3>
            </div>
            <p className="text-xs text-zinc-600">Tragen Sie sich ein — wir informieren Sie, sobald wir bei Ihnen starten.</p>

            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Vorname *" required className="h-11 px-3 rounded-lg border border-black/10 text-sm" data-testid="cs-first-name" />
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nachname *" required className="h-11 px-3 rounded-lg border border-black/10 text-sm" data-testid="cs-last-name" />
            </div>

            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-Mail *" required className="w-full h-11 px-3 rounded-lg border border-black/10 text-sm" data-testid="cs-email" />

            <div className="grid grid-cols-3 gap-3">
              <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value, city: "" })} placeholder="PLZ *" required className="col-span-1 h-11 px-3 rounded-lg border border-black/10 text-sm" data-testid="cs-zip" />
              <div className="col-span-2 relative">
                <input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Ort *"
                  required
                  className="w-full h-11 px-3 rounded-lg border border-black/10 text-sm"
                  data-testid="cs-city"
                  list="cs-cities"
                />
                <datalist id="cs-cities">
                  {cityOptions.map((o) => (
                    <option key={`${o.zip}-${o.city}`} value={o.city}>{o.zip} · {o.city}</option>
                  ))}
                </datalist>
                {cityOptions.length > 0 && cityOptions.length <= 5 && !form.city && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-black/10 rounded-lg shadow-lg z-10 max-h-40 overflow-auto" data-testid="cs-city-suggestions">
                    {cityOptions.map((o, i) => (
                      <button
                        key={`${o.zip}-${o.city}`}
                        type="button"
                        onClick={() => setForm({ ...form, city: o.city, zip: o.zip })}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50"
                        data-testid={`cs-city-option-${i}`}
                      >
                        <span className="font-mono text-xs text-zinc-500 mr-2">{o.zip}</span> {o.city}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="Straße & Hausnummer" className="w-full h-11 px-3 rounded-lg border border-black/10 text-sm" data-testid="cs-street" />

            <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={form.privacy_ack} onChange={(e) => setForm({ ...form, privacy_ack: e.target.checked })} className="mt-0.5" required data-testid="cs-privacy-ack" />
              <span>
                Ich willige ein, dass {tenant.name || "der Anbieter"} meine oben angegebenen Daten speichert und mich per E-Mail über den Servicebeginn informiert. *
                {" "}
                <a href="/legal/datenschutz" target="_blank" rel="noreferrer" className="underline hover:text-black">Datenschutzerklärung</a>
              </span>
            </label>

            <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={form.newsletter_optin} onChange={(e) => setForm({ ...form, newsletter_optin: e.target.checked })} className="mt-0.5" data-testid="cs-newsletter-optin" />
              <span>Ich möchte zusätzlich den Newsletter mit Tipps und Aktionen erhalten (jederzeit abbestellbar).</span>
            </label>

            <button type="submit" disabled={sending} className="w-full h-12 rounded-lg font-bold text-white shadow-lg transition disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)` }} data-testid="cs-submit">
              {sending ? "Sende…" : ctaText}
            </button>

            {status && (
              <div className={`text-sm p-3 rounded ${status.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="cs-feedback">
                {status.msg}
              </div>
            )}
            <p className="text-[11px] text-zinc-500">* Pflichtfelder. Ihre Daten werden ausschließlich zur Kontaktaufnahme durch {tenant.name || "uns"} verwendet.</p>
          </form>
        )}

        {showContact && (tenant.phone || tenant.email) && (
          <div className="mt-10 flex items-center gap-6 text-sm text-zinc-500">
            {tenant.phone && <a href={`tel:${tenant.phone.replace(/\s/g, "")}`} className="hover:text-black inline-flex items-center gap-1"><Phone size={14} /> {tenant.phone}</a>}
            {tenant.email && <a href={`mailto:${tenant.email}`} className="hover:text-black">{tenant.email}</a>}
          </div>
        )}
      </div>
    </div>
  );
}
